import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { useMemo, useState } from "react";
import { exoraToast } from '@/lib/exoraToast';
import { CheckCircle2, X, AlertCircle } from "lucide-react";
import { z } from 'zod';
import { apiClient } from '@/lib/apiClient';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './AccessModal.css';
import { PhoneCountrySelect } from "@/components/checkout/PhoneCountrySelect";
import { Flag } from "@/utils/flag";
import { Capacitor } from "@capacitor/core";

const requestAccessSchema = z.object({
  fullName: z.string()
    .trim()
    .min(3, 'Full name must be at least 3 characters')
    .max(100, 'Full name must be less than 100 characters')
    .regex(/^[a-zA-Z\s'-]+$/, 'Name contains invalid characters')
    .refine(
      (name) => name.trim().split(/\s+/).length >= 2,
      'Please enter your full name (first and last name)'
    ),
  email: z.string()
    .trim()
    .toLowerCase()
    .email('Please enter a valid email address')
    .max(255, 'Email must be less than 255 characters')
    .refine(
      (email) => {
        const parts = email.split('@');
        if (parts.length !== 2) return false;
        const domain = parts[1];
        return domain.includes('.') && domain.split('.').pop()!.length >= 2;
      },
      'Email must have a valid domain (e.g., example.com)'
    ),
  phone: z.string()
    .refine(
      (value) => {
        if (!value) return true; // Optional field
        const digits = value.replace(/\D/g, '');
        // Bare dial code (≤3 digits, e.g. "+44") counts as empty/optional
        if (digits.length <= 3) return true;
        try {
          return isValidPhoneNumber(value);
        } catch {
          return false;
        }
      },
      'Please enter a valid phone number'
    )
    .optional(),
  reason: z.string()
    .trim()
    .min(10, 'Please provide at least 10 characters')
    .max(500, 'Reason must be less than 500 characters')
});

interface AccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const DisabledPhoneCountrySelect = ({ value, disabled }: { value?: string; disabled?: boolean }) => {
  return (
    <span
      aria-hidden="true"
      className="mr-2 flex items-center bg-transparent px-0 py-1"
      data-disabled={disabled ? 'true' : 'false'}
    >
      <Flag code={value || 'GB'} size={18} />
    </span>
  );
};

export const AccessModal = ({ open, onOpenChange }: AccessModalProps) => {
  const isNative = Capacitor.isNativePlatform();
  const isNativeIos = Capacitor.getPlatform() === 'ios';
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    reason: "",
    accessCode: ""
  });
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [approved, setApproved] = useState(false);
  const [codeInvalid, setCodeInvalid] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});


  const accessPhoneCountrySelect = useMemo(
    () => (isNativeIos ? DisabledPhoneCountrySelect : PhoneCountrySelect),
    [isNativeIos]
  );

  // Returns true when the phone field should be treated as "empty" for an optional field.
  // Covers: empty string, whitespace, or a bare dial code like "+44" / "+966" with no national digits.
  const isPhoneEffectivelyEmpty = (raw: string | undefined | null) => {
    if (!raw) return true;
    const trimmed = raw.trim();
    if (!trimmed) return true;
    // Strip leading + and all non-digits — if nothing remains beyond the country code,
    // treat as empty. We check national digits = total digits minus dial code length.
    const digits = trimmed.replace(/\D/g, '');
    // Country dial codes are 1–3 digits. If user only has the dial code (≤3 digits), treat as empty.
    return digits.length <= 3;
  };

  const validateField = (fieldName: keyof typeof formData) => {
    // Phone is optional — treat empty / whitespace / bare dial code as valid
    if (fieldName === 'phone' && isPhoneEffectivelyEmpty(formData.phone)) {
      setValidationErrors(prev => ({ ...prev, phone: '' }));
      setErrors(prev => ({ ...prev, phone: '' }));
      return;
    }
    try {
      const fieldSchema = requestAccessSchema.shape[fieldName as keyof typeof requestAccessSchema.shape];
      if (fieldSchema) {
        fieldSchema.parse(formData[fieldName]);
        setValidationErrors(prev => ({ ...prev, [fieldName]: '' }));
        setErrors(prev => ({ ...prev, [fieldName]: '' }));
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errorMessage = error.errors[0]?.message || 'Invalid value';
        setValidationErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
        setErrors(prev => ({ ...prev, [fieldName]: errorMessage }));
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setValidationErrors({});
    
    // Validate inputs
    const result = requestAccessSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      setValidationErrors(fieldErrors);
      exoraToast.error(result.error.errors[0].message);
      return;
    }

    setLoading(true);
    
    try {
      // Split full name into first and last name
      const nameParts = result.data.fullName.trim().split(/\s+/);
      const firstName = nameParts[0];
      const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      // Collect device metadata
      const userAgent = navigator.userAgent;
      const { getBrowserName, getDeviceType, getDeviceName } = await import('@/utils/deviceInfo');
      const browser = getBrowserName(userAgent);
      const deviceType = `${getDeviceName(userAgent)} (${getDeviceType(userAgent)})`;

      // Attempt to get IP via public API
      let ipAddress = 'Unknown';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        ipAddress = ipData.ip || 'Unknown';
      } catch {
        // ignore
      }

      // Normalize phone: bare dial codes (e.g. "+44") should be treated as no phone
      const normalizedPhone = isPhoneEffectivelyEmpty(result.data.phone) ? null : result.data.phone;

      // Check for duplicate email/phone before submitting
      const { data: dupCheck, error: dupError } = await apiClient.invoke('check-duplicate-signup', { email: result.data.email, phone: normalizedPhone });

      if (dupError) {
        console.error('Duplicate check failed:', dupError);
        throw new Error('Could not verify your details. Please try again.');
      }

      if (dupCheck?.duplicate) {
        exoraToast.error(dupCheck.message);
        setLoading(false);
        return;
      }

      // Submit via edge function (bypasses RLS)
      const trimmedCode = formData.accessCode.trim().toUpperCase();
      const { data: submitData, error: submitError } = await apiClient.invoke('submit-access-request', {
        firstName,
        lastName,
        email: result.data.email,
        phone: normalizedPhone,
        reason: result.data.reason || null,
        ipAddress,
        userAgent,
        ...(trimmedCode ? { accessCode: trimmedCode } : {}),
      });

      if (submitError) {
        console.error('Submit error:', submitError);
        throw new Error('Failed to submit access request');
      }

      if (submitData?.error) {
        exoraToast.error(submitData.error);
        return;
      }

      const isApproved = submitData?.approved === true;
      const isCodeInvalid = submitData?.code_invalid === true;

      // Fire-and-forget: send emails in background (don't block UI).
      // Skip for approved path — send-invite-email already handles it.
      if (!isApproved) {
        apiClient.fireAndForget('send-access-request-emails', {
          firstName,
          lastName,
          email: result.data.email,
          phone: normalizedPhone,
          reason: result.data.reason,
          ipAddress,
          userAgent,
          deviceType,
          browser,
          requestId: submitData?.data?.[0]?.id,
        });
      }

      if (isApproved) {
        exoraToast.success({ title: 'Welcome to Exora', description: 'Check your inbox for your invitation.' });
      } else {
        exoraToast.success({ title: 'Access request submitted', description: 'Please check your email for next steps.' });
      }

      setSubmittedEmail(result.data.email);
      setApproved(isApproved);
      setCodeInvalid(isCodeInvalid);
      setShowSuccess(true);
      setFormData({ fullName: "", email: "", phone: "", reason: "", accessCode: "" });

    } catch (error) {
      console.error('Error submitting access request:', error);
      exoraToast.error({ title: "Couldn't submit your request", description: 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!showSuccess) {
      onOpenChange(false);
      setFormData({ fullName: "", email: "", phone: "", reason: "", accessCode: "" });
      setErrors({});
      setValidationErrors({});
    }
  };

  return (
    <>
      <Dialog open={open && !showSuccess} onOpenChange={handleClose} modal={!isNative}>
        <DialogContent className="border border-border/60 text-foreground w-full max-w-[calc(100vw-24px)] sm:max-w-[calc(100vw-32px)] md:max-w-md p-0 max-h-[90vh] overflow-y-auto overflow-x-visible rounded-2xl" style={{ background: 'hsl(var(--surface-2))' }}>
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 text-muted-foreground hover:text-foreground transition-colors z-10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="px-6 sm:px-8 pt-7 sm:pt-8 pb-0">
            <DialogHeader className="space-y-2 relative">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[10px] uppercase tracking-[0.35em] text-gold/60 font-medium">Private access</p>
              </div>
              <DialogTitle className="text-xl font-semibold text-foreground tracking-tight">
                Request access
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                We onboard approved clients within 24-48 hours
              </DialogDescription>
            </DialogHeader>
          </div>

          {/* Divider */}
          <div className="mx-6 sm:mx-8 my-5 h-px bg-gradient-to-r from-transparent via-border/60 to-transparent" />

          <div className="px-6 sm:px-8 pb-7 sm:pb-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Full Name Field */}
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                  Full name <span className="text-gold ml-0.5">*</span>
                </label>
                <input
                  id="fullName"
                  type="text"
                  autoComplete="name"
                  inputMode="text"
                  spellCheck={false}
                  className={`w-full rounded-md bg-background/50 border ${
                    errors.fullName ? 'border-destructive/40 ring-1 ring-destructive/30' : 'border-border/50'
                  } px-4 py-3 text-foreground outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-all placeholder:text-muted-foreground/30 h-11 text-sm`}
                  style={{ fontSize: '16px', caretColor: 'hsl(var(--gold))' }}
                  placeholder="First and last name"
                  value={formData.fullName}
                  onChange={(e) => {
                    const value = e.target.value.replace(/^\s+/, '').replace(/[0-9]/g, '');
                    setFormData({ ...formData, fullName: value });
                    setErrors({ ...errors, fullName: "" });
                  }}
                  required
                />
                {errors.fullName && (
                  <p className="text-[11px] text-destructive px-0.5">{errors.fullName}</p>
                )}
              </div>

              {/* Email Field */}
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                  Email <span className="text-gold ml-0.5">*</span>
                </label>
                <div className="relative">
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    spellCheck={false}
                    className={`w-full rounded-md bg-background/50 border px-4 py-3 pr-10 text-foreground outline-none transition-all placeholder:text-muted-foreground/30 h-11 text-sm ${
                      validationErrors.email ? 'border-destructive/40 ring-1 ring-destructive/30 focus:border-destructive/50' : 'border-border/50 focus:border-gold/50 focus:ring-1 focus:ring-gold/20'
                    }`}
                    style={{ fontSize: '16px', caretColor: 'hsl(var(--gold))' }}
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => {
                      const value = e.target.value.toLowerCase().replace(/^\s+/, '');
                      setFormData({ ...formData, email: value });
                      if (validationErrors.email) {
                        setValidationErrors({ ...validationErrors, email: "" });
                        setErrors({ ...errors, email: "" });
                      }
                    }}
                    onBlur={() => validateField('email')}
                    required
                  />
                  {validationErrors.email ? (
                    <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                  ) : formData.email.includes('@') && formData.email.includes('.') ? (
                    <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" />
                  ) : null}
                </div>
                {validationErrors.email && (
                  <p className="text-[11px] text-destructive px-0.5 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.email}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-[10px] tracking-[0.2em] text-ivory/40 uppercase tracking-widest">Phone <span className="text-ivory/25">(Optional)</span></label>
                <div className="relative">
                  <PhoneInput international value={formData.phone}
                    onChange={value => { if (!value || value.replace(/\D/g, '').length <= 15) { setFormData(prev => ({ ...prev, phone: value || '' })); if (validationErrors.phone) setValidationErrors({ ...validationErrors, phone: '' }); } }}
                    onBlur={() => validateField('phone')}
                    countrySelectComponent={accessPhoneCountrySelect}
                    className={`phone-input-checkout ${validationErrors.phone ? 'phone-error' : formData.phone && !isPhoneEffectivelyEmpty(formData.phone) && isValidPhoneNumber(formData.phone) ? 'phone-valid-emerald' : ''}`}
                  />
                </div>
                {validationErrors.phone && <p className="text-[10px] text-destructive tracking-wide">{validationErrors.phone}</p>}
                {!validationErrors.phone && <p className="text-[10px] text-muted-foreground/50 tracking-wide">Optional · Include country code · e.g. +44 7700 900000</p>}
              </div>

              {/* Reason Field */}
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                  How do you plan to use Exora? <span className="text-gold ml-0.5">*</span>
                </label>
                <textarea
                  id="reason"
                  className={`w-full rounded-md bg-background/50 border ${
                    errors.reason ? 'border-destructive/40 ring-1 ring-destructive/30' : 'border-border/50'
                  } px-4 py-3 text-foreground text-sm outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-all min-h-[100px] resize-none placeholder:text-muted-foreground/30`}
                  placeholder="Tell us about yourself..."
                  value={formData.reason}
                  onChange={(e) => {
                    setFormData({ ...formData, reason: e.target.value });
                    setErrors({ ...errors, reason: "" });
                  }}
                />
                {errors.reason && (
                  <p className="text-[11px] text-destructive px-0.5">{errors.reason}</p>
                )}
              </div>

              {/* Access Code Field (optional) */}
              <div className="space-y-1.5">
                <label className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground/70 font-medium">
                  Access code <span className="text-muted-foreground/40 normal-case tracking-normal">(optional)</span>
                </label>
                <input
                  id="accessCode"
                  type="text"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  maxLength={32}
                  className="w-full rounded-md bg-background/50 border border-border/50 px-4 py-3 text-foreground outline-none focus:border-gold/50 focus:ring-1 focus:ring-gold/20 transition-all placeholder:text-muted-foreground/30 h-11 text-sm tracking-[0.15em] uppercase"
                  style={{ fontSize: '16px', caretColor: 'hsl(var(--gold))' }}
                  placeholder="If you were given one"
                  value={formData.accessCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase().replace(/\s+/g, '');
                    setFormData({ ...formData, accessCode: value });
                  }}
                />
                <p className="text-[10px] text-muted-foreground/50 tracking-wide">Have an invitation code? Enter it for instant access.</p>
              </div>


              {/* Submit Button */}
              <div className="pt-1">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-xl font-medium text-[13px] tracking-[0.1em] uppercase relative overflow-hidden group transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.97]"
                  style={{
                    background: 'linear-gradient(135deg, hsl(var(--gold) / 0.1), hsl(var(--gold) / 0.03))',
                    border: '1px solid hsl(var(--gold) / 0.4)',
                    color: 'hsl(var(--gold))',
                    boxShadow: '0 0 24px -8px hsl(var(--gold) / 0.2), inset 0 1px 0 hsl(var(--gold) / 0.12)',
                  }}
                >
                  <span className="relative z-10">
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="w-4 h-4 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                        Submitting
                      </span>
                    ) : (
                      'Request access'
                    )}
                  </span>
                  <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ background: 'linear-gradient(135deg, hsl(var(--gold) / 0.15), transparent)' }} />
                  <span className="absolute inset-0 bg-gradient-to-r from-transparent via-[hsl(var(--gold)/0.12)] to-transparent -translate-x-full animate-[shimmerSweep_3s_ease-in-out_infinite]" />
                </button>
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            style={{ background: 'hsl(var(--bg-app))' }}
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[350px] rounded-full bg-gold/[0.04] blur-[100px] pointer-events-none" />

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-[420px] rounded-3xl border border-gold/10 overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, hsl(var(--bg-elevated)), hsl(var(--surface-2)))',
                boxShadow: '0 30px 80px -20px rgba(0,0,0,0.6), 0 0 0 1px hsl(var(--gold) / 0.03)'
              }}
            >
              <button
                onClick={() => {
                  setShowSuccess(false);
                  onOpenChange(false);
                }}
                className="absolute top-5 right-5 text-ivory/40 hover:text-ivory transition-colors z-10"
                aria-label="Close success modal"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="p-10 sm:p-12 text-center relative">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className="mx-auto mb-6 w-16 h-16 rounded-full flex items-center justify-center relative"
                  style={{
                    background: 'radial-gradient(circle at 30% 30%, hsl(var(--gold) / 0.18), hsl(var(--gold) / 0.08))',
                    border: '1px solid hsl(var(--gold) / 0.12)',
                    boxShadow: '0 0 40px -10px hsl(var(--gold) / 0.18)'
                  }}
                >
                  <CheckCircle2 className="w-8 h-8 text-gold" strokeWidth={1.5} />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.45 }}
                >
                  <p className="text-[10px] uppercase tracking-[0.35em] text-gold/50 mb-3 font-medium">
                    {approved ? "You're in" : 'Application received'}
                  </p>
                  <h2 className="text-3xl sm:text-[2rem] font-semibold text-foreground tracking-tight mb-4 leading-[1.05]">
                    {approved ? 'Welcome to Exora' : 'Welcome aboard'}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-[320px] mx-auto">
                    {approved ? (
                      <>Your invitation is on its way to <span className="text-gold font-medium">{submittedEmail}</span> — check your inbox to activate your account.</>
                    ) : (
                      <>Our exclusive concierge team will review your application and reach out within <span className="text-gold font-medium">24-48 hours</span></>
                    )}
                  </p>
                  {codeInvalid && !approved && (
                    <p className="mt-3 text-[11px] text-muted-foreground/70 leading-relaxed max-w-[300px] mx-auto italic">
                      We didn't recognise that access code, so your request will be reviewed as usual.
                    </p>
                  )}

                </motion.div>

                {/* Divider */}
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  className="my-6 mx-auto w-12 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent"
                />

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35, duration: 0.5 }}
                  className="space-y-4"
                >
                  <p className="text-sm text-muted-foreground">
                    Confirmation sent to <span className="text-foreground font-medium">{submittedEmail}</span>
                  </p>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.45, duration: 0.4 }}
                    className="rounded-xl border border-border/40 px-5 py-3.5 inline-flex items-center gap-2"
                    style={{ background: 'hsl(var(--background) / 0.5)' }}
                  >
                    <span className="text-gold text-xs">&#10022;</span>
                    <span className="text-sm text-muted-foreground">Check your inbox for confirmation</span>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.55, duration: 0.45 }}
                    className="pt-4"
                  >
                    <button
                      onClick={() => {
                        setShowSuccess(false);
                        onOpenChange(false);
                      }}
                      className="px-10 py-3 rounded-xl font-medium text-[13px] tracking-[0.15em] uppercase relative overflow-hidden group transition-all duration-200 active:scale-[0.97]"
                      style={{
                        background: 'linear-gradient(135deg, hsl(var(--gold) / 0.1), hsl(var(--gold) / 0.03))',
                        border: '1px solid hsl(var(--gold) / 0.4)',
                        color: 'hsl(var(--gold))',
                        boxShadow: '0 0 24px -8px hsl(var(--gold) / 0.2), inset 0 1px 0 hsl(var(--gold) / 0.12)',
                      }}
                    >
                      <span className="relative z-10">Got it</span>
                      <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-150" style={{ background: 'linear-gradient(135deg, hsl(var(--gold) / 0.15), transparent)' }} />
                      <span className="absolute inset-0 bg-gradient-to-r from-transparent via-[hsl(var(--gold)/0.12)] to-transparent -translate-x-full animate-[shimmerSweep_3s_ease-in-out_infinite]" />
                    </button>
                  </motion.div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
